import type { IAuth } from "@/models/Auth";
import type { IGeneral } from "@/models/General";
import { Box, Grid, Select, TextInput } from "@dehub/theme";
import { LockSimpleIcon } from "@phosphor-icons/react";
import type { UseFormReturnType } from "@dehub/theme";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import classes from "../../PartnerPage.module.css";
import { DefinitionList, type DefinitionItem } from "../DefinitionList";
import { formatDate } from "@/utils/format-date";
import type { IFormData } from "./org-form-model";

type Props = {
  form: UseFormReturnType<IFormData>;
  isEdit: boolean;
};

type Named = { code?: string; id?: string; name?: string };

const nameOf = (list: Named[] | undefined, value: string) =>
  value ? ((list ?? []).find((o) => (o.code ?? o.id) === value)?.name ?? value) : "";

function LockMark({ label }: Readonly<{ label: string }>) {
  return <LockSimpleIcon className={classes.lockedIcon} size={16} weight="bold" role="img" aria-label={label} />;
}

export function MainInfoIdentitySection({ form, isEdit }: Props) {
  const { t } = useTranslation("partner");
  const { t: tCommon } = useTranslation("common");
  const { equityTypes, legalEntityTypes, partnerClassifications, businessSectors } = useSelector(
    (state: { general: IGeneral }) => state.general,
  );
  const { user } = useSelector((state: { auth: IAuth }) => state.auth);

  const values = form.values;
  const registerStatus = user?.partner?.registerStatus;
  const regNumberDisabled = registerStatus === "VERIFIED" || registerStatus === "ADMIN_VERIFIED";
  const isCitizen = user?.partner?.type === "CITIZEN";
  // For a citizen the partner.businessName only holds the given name; show the
  // full "овог нэр" from the logged-in citizen's lastName + firstName.
  const citizenFullName = [user?.lastName, user?.firstName].filter(Boolean).join(" ");
  const businessName = isCitizen && citizenFullName ? citizenFullName : values.businessName;
  const bizNameLabel = t(isCitizen ? "bizNameCitizen" : "bizName");
  const bizNameLatinLabel = t(isCitizen ? "bizNameLatinCitizen" : "fields.latin");
  const classificationLabel = t(isCitizen ? "partnerCategoryCitizen" : "partnerCategory");
  const regDate = formatDate(user?.partnerCreatedAt);

  const sectorOptions = (businessSectors ?? [])
    .map((o: any) => ({ label: o?.name ?? "", value: o?.id ?? o?.code ?? "" }))
    .filter((o) => o.value);
  const classificationOptions = (partnerClassifications ?? [])
    .map((o: any) => ({ label: o?.name ?? "", value: o?.code ?? "" }))
    .filter((o) => o.value);

  if (!isEdit) {
    const items: (DefinitionItem | null)[] = [
      { key: "regNumber", label: t("fields.regNumber"), value: values.regNumber, locked: regNumberDisabled },
      {
        key: "orgCode",
        label: t("orgCode"),
        value: user?.partnerRefCode ? <span translate="no">{user.partnerRefCode}</span> : "",
        locked: true,
      },
      { key: "regDate", label: t("fields.regDate"), value: regDate, locked: true },
      { key: "businessName", label: bizNameLabel, value: businessName, locked: true },
      { key: "businessNameEng", label: bizNameLatinLabel, value: values.businessNameEng },
      isCitizen
        ? null
        : {
            key: "legalEntityType",
            label: t("legalEntityType"),
            value: nameOf(legalEntityTypes as Named[], values.legalEntityType),
          },
      isCitizen
        ? null
        : { key: "equityType", label: t("equityType"), value: nameOf(equityTypes as Named[], values.equityType) },
      { key: "country", label: t("country"), value: values.country },
      {
        key: "classification",
        label: classificationLabel,
        value: isCitizen
          ? nameOf(businessSectors as Named[], values.businessSectorId)
          : nameOf(partnerClassifications as Named[], values.classification),
      },
    ];
    return <DefinitionList items={items.filter((i): i is DefinitionItem => i !== null)} />;
  }

  return (
    <Box className={classes.editBody}>
      <Grid gap="sm">
        <Grid.Col span={{ base: 12, md: 6 }}>
          <TextInput
            readOnly={regNumberDisabled}
            className={regNumberDisabled ? classes.locked : undefined}
            rightSection={regNumberDisabled ? <LockMark label={t("lockedHint")} /> : undefined}
            label={t("fields.regNumber")}
            required={!regNumberDisabled}
            {...form.getInputProps("regNumber")}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <TextInput
            readOnly
            className={classes.locked}
            rightSection={<LockMark label={t("lockedHint")} />}
            label={t("orgCode")}
            translate="no"
            value={user?.partnerRefCode ?? ""}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <TextInput
            readOnly
            className={classes.locked}
            rightSection={<LockMark label={t("lockedHint")} />}
            label={t("fields.regDate")}
            value={regDate}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <TextInput
            readOnly
            className={classes.locked}
            rightSection={<LockMark label={t("lockedHint")} />}
            label={bizNameLabel}
                        {...form.getInputProps("businessName")}
            {...(isCitizen && citizenFullName ? { value: citizenFullName } : {})}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <TextInput
            label={bizNameLatinLabel}
            placeholder="NEXT Distribution LLC"
            spellCheck={false}
            {...form.getInputProps("businessNameEng")}
          />
        </Grid.Col>
        {!isCitizen && (
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Select
              data={(legalEntityTypes ?? []).map((c: any) => ({ label: c.name, value: c.code }))}
              label={t("legalEntityType")}
              placeholder={tCommon("select")}
              required
              {...form.getInputProps("legalEntityType")}
            />
          </Grid.Col>
        )}
        {!isCitizen && (
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Select
              data={(equityTypes ?? []).map((c: any) => ({ label: c.name, value: c.code }))}
              label={t("equityType")}
              placeholder={tCommon("select")}
              required
              {...form.getInputProps("equityType")}
            />
          </Grid.Col>
        )}
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Select
            data={[{ label: t("countryMongolia"), value: "Монгол" }]}
            label={t("country")}
            placeholder={tCommon("select")}
            required
            {...form.getInputProps("country")}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          {isCitizen ? (
            // "Үйл ажиллагааны чиглэл" = business sector (stored on the business),
            // not the corporate/SME partner classification.
            <Select
              data={sectorOptions}
              label={classificationLabel}
              placeholder={tCommon("select")}
              searchable
              required
              {...form.getInputProps("businessSectorId")}
            />
          ) : (
            <Select
              data={classificationOptions}
              label={classificationLabel}
              placeholder={tCommon("select")}
              required
              {...form.getInputProps("classification")}
            />
          )}
        </Grid.Col>
      </Grid>
    </Box>
  );
}
